<template>
  <TableCell ref="tableCell" 
    :tag="tag"
    :popoverScroll="false"
    :popoverPadding="popoverPadding"
    v-model:showingPopover="showingPopover"
  >
    <template v-if="!tag.enabled.value">
      Disabled
    </template>
    <template v-else-if="tag.commError.value">
      Comm error
    </template>
    <span v-else style="white-space: nowrap;">
      {{ convertedValueString }}
      <span v-html="convertedUnitHtml(unit)" style="font-weight: 300;"></span>
    </span>
    <template #PopoverContentDesktop>
      <component :is="popoverComponent" v-bind="popoverComponentProps"></component>
    </template>
  </TableCell>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { convertValue, convertUnit, convertedUnitHtml, unitRound, unitRoundForStatusPage } from "./units.js";
import { changeSettings, openHistory } from "./appUtility.js";
import { swap } from "./utility.js";
import store from "./store.js";
import TableCell from "./TableCell.vue";
import TieredMenu from "primevue/tieredmenu";
import AggregateStatusPopoverCalibration from "./AggregateStatusPopoverCalibration.vue";

let props = defineProps([
  "tag",
  "selectedCells",
  "showingPopover",
  "showingCalibrationKeypad"
]);

let emit = defineEmits([
  "calibrate"
]);

let showingPopover = defineModel("showingPopover");
let showingCalibrationKeypad = defineModel("showingCalibrationKeypad");

let aggr = computed(() => {
  return props.tag.$parent.$parent;
});

let unit = computed(() => {
  let system = props.tag.$session.system;
  let parents = props.tag.$parents();
  let aggregate = parents.find(parent => parent.sensors);
  let sensorType = system.sensorTypes.value[aggregate.sensorType.value];
  return sensorType.unit;
});

let convertedValue = computed(() => {
  return convertValue(props.tag.$value(), unit.value);
});

let convertedValueString = computed(() => {
  let convertedUnit = convertUnit(unit.value);
  return unitRoundForStatusPage(convertedValue.value, convertedUnit, props.tag.$precision());
});

let tableCell = ref();
let popoverComponent = computed(() => {
  if (showingPopover.value && props.showingCalibrationKeypad)
    return AggregateStatusPopoverCalibration;
  else if (showingPopover.value)
    return TieredMenu;
  else
    return null;
});

watch(popoverComponent, async () => {
  if (showingPopover.value)
    await tableCell.value.repositionPopover();
});

let popoverComponentProps = computed(() => {
  if (popoverComponent.value == TieredMenu)
    return popoverComponentPropsMenu();
  if (popoverComponent.value == AggregateStatusPopoverCalibration)
    return popoverComponentPropsCalibration();
});

let popoverPadding = computed(() => {
  if (popoverComponent.value == TieredMenu)
    return "0px";
  else
    return "20px";
});

function popoverComponentPropsMenu() {
  let menuItems = [
    {
      label: "View history",
      command: () => openHistory(props.tag)
    },
    { 
      label: "Set priority",
      visible: aggr.value.method.value == "prio",
      items: aggr.value.sensors.children.map((sensor, index) => {
        return {
          label: `Priority ${index + 1}`,
          command: () => setPriority(index),
          icon: (index == props.tag.$key()) ? "pi pi-check" : "pi"
        };
      })
    },
    {
      label: "Calibrate",
      visible: store.showAdvanced,
      disabled: props.tag.commError.value,
      command: () => showingCalibrationKeypad.value = true
    },
    {
      label: props.tag.enabled.value ? "Disable" : "Enable",
      visible: aggr.value.sensors.children.length >= 2,
      command: () => toggleSensorEnabled()
    },
  ];
  return {
    model: menuItems
  };
}

function popoverComponentPropsCalibration() {
  let sensor = props.tag;
  return {
    sensor,
    popoverTitle: sensor.name.value,
    showChannel: false,
    value: convertedValue.value,
    onCancel: () => showingPopover.value = false,
    onOk: () => showingPopover.value = false
  }
}

function setPriority(value) {
  let sensor = props.tag;
  let newIndex = value;
  let oldIndex = sensor.$key();
  let sensors = aggr.value.sensors;
  let sensorHandles = sensors.children.map(sensor => ({ handle: sensor.handle }));
  swap(sensorHandles, oldIndex, newIndex);

  showingPopover.value = false;
  changeSettings(props.selectedCells, cell => [{
    handle: sensors.handle,
    value: sensorHandles
  }]);
}

function toggleSensorEnabled() {
  let sensor = props.tag;
  showingPopover.value = false;
  changeSettings(props.selectedCells, cell => [{
    handle: sensor.enabled.handle,
    value: !sensor.enabled.value
  }]);
}

</script>